import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Slider from '@mui/material/Slider';
import { keyframes } from '@mui/system';

// ── Sentiment palette ──────────────────────────────────────────────────────
const S = {
  positive: { bg: '#22c55e', dim: 'rgba(34,197,94,0.10)', glow: 'rgba(34,197,94,0.55)', border: 'rgba(34,197,94,0.35)', label: '+ Positive' },
  negative: { bg: '#E61D50', dim: 'rgba(230,29,80,0.10)',  glow: 'rgba(230,29,80,0.55)',  border: 'rgba(230,29,80,0.35)',  label: '− Negative' },
  neutral:  { bg: '#6b7280', dim: 'rgba(107,114,128,0.08)', glow: 'rgba(107,114,128,0.4)', border: 'rgba(107,114,128,0.25)', label: '= Neutral'  },
};

const SPEAKER_PALETTE = [
  '#818cf8','#34d399','#fb923c','#f472b6',
  '#60a5fa','#a78bfa','#4ade80','#fbbf24','#e879f9','#38bdf8',
];

const pulse = keyframes`
  0%,100% { opacity:1; transform:scale(1); }
  50%      { opacity:0.7; transform:scale(0.92); }
`;

const playingGlow = (glow) => keyframes`
  0%,100% { box-shadow: 0 0 0 1.5px ${glow}, 0 0 16px ${glow}; }
  50%      { box-shadow: 0 0 0 2px ${glow}, 0 0 32px ${glow}, 0 0 56px ${glow}40; }
`;

// ── Helpers ────────────────────────────────────────────────────────────────
const sentimentOf = (row) => {
  const k = (row.sentiment || '').toLowerCase();
  return S[k] || S.neutral;
};

const fmt = (s) => {
  if (!s && s !== 0) return '0:00';
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// ── Component ──────────────────────────────────────────────────────────────
const MosaicView = ({ rows }) => {
  const [zoom, setZoom]             = useState(1);
  const [selected, setSelected]     = useState(null); // { row, clipIdx }
  const [isPlaying, setIsPlaying]   = useState(false);
  const [progress, setProgress]     = useState(0);    // 0–100
  const videoRef                    = useRef(null);
  const rafRef                      = useRef(null);

  const clips = useMemo(() => rows.filter(r => !r.space), [rows]);

  const speakers = useMemo(() => {
    const unique = [...new Set(clips.map(r => r.speaker).filter(Boolean))];
    return Object.fromEntries(unique.map((sp, i) => [sp, SPEAKER_PALETTE[i % SPEAKER_PALETTE.length]]));
  }, [clips]);

  const durations = useMemo(() =>
    clips.map(r => Math.max(1, (r.end_in ?? 0) - (r.start_in ?? 0))),
  [clips]);

  const maxDur   = useMemo(() => Math.max(...durations, 1), [durations]);
  const totalDur = useMemo(() => durations.reduce((a, b) => a + b, 0), [durations]);

  const tileW = (dur) => Math.round(44 + (dur / maxDur) * 220);
  const tileH = Math.round(72 * zoom);

  // ── Playback ───────────────────────────────────────────────────────────
  const stopLoop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const startLoop = useCallback((vid, startIn, endIn) => {
    stopLoop();
    const check = () => {
      if (!vid) return;
      const t = vid.currentTime;
      const dur = endIn - startIn;
      setProgress(dur > 0 ? Math.min(100, ((t - startIn) / dur) * 100) : 0);
      if (t >= endIn) {
        vid.pause();
        setIsPlaying(false);
        setProgress(100);
        return;
      }
      rafRef.current = requestAnimationFrame(check);
    };
    rafRef.current = requestAnimationFrame(check);
  }, [stopLoop]);

  const playClip = useCallback((row) => {
    const vid = videoRef.current;
    if (!vid || !row.video) return;
    stopLoop();
    vid.src = row.video;
    vid.currentTime = row.start_in ?? 0;
    setProgress(0);
    vid.addEventListener('seeked', () => {
      vid.play().catch(() => {});
      setIsPlaying(true);
      startLoop(vid, row.start_in ?? 0, row.end_in ?? (row.start_in + 30));
    }, { once: true });
  }, [startLoop, stopLoop]);

  const togglePlay = useCallback(() => {
    const vid = videoRef.current;
    if (!vid || !selected) return;
    if (isPlaying) {
      vid.pause();
      stopLoop();
      setIsPlaying(false);
    } else {
      vid.play().catch(() => {});
      setIsPlaying(true);
      startLoop(vid, selected.row.start_in ?? 0, selected.row.end_in ?? (selected.row.start_in + 30));
    }
  }, [isPlaying, selected, startLoop, stopLoop]);

  const selectClip = useCallback((row, idx) => {
    setSelected({ row, clipIdx: idx });
    playClip(row);
  }, [playClip]);

  const stepClip = useCallback((dir) => {
    if (!selected) return;
    const next = selected.clipIdx + dir;
    if (next < 0 || next >= clips.length) return;
    selectClip(clips[next], next);
  }, [selected, clips, selectClip]);

  useEffect(() => () => stopLoop(), [stopLoop]);

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
      background: 'radial-gradient(ellipse 80% 40% at 50% -10%, rgba(230,29,80,0.07) 0%, transparent 60%), #07070a',
    }}>

      {/* ── Controls bar ───────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2.5, px: 2.5, py: 1,
        borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, flexWrap: 'wrap',
      }}>
        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {Object.values(S).map(({ bg, label }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: bg, boxShadow: `0 0 5px ${bg}` }} />
              <Typography sx={{ fontSize: '9px', color: 'rgba(255,255,255,0.35)', fontWeight: 600, letterSpacing: '0.04em' }}>{label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Speaker legend */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {Object.entries(speakers).map(([sp, color]) => (
            <Box key={sp} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Box sx={{ width: 12, height: 2, background: color, borderRadius: 1 }} />
              <Typography sx={{ fontSize: '9px', color, fontWeight: 700 }}>{sp}</Typography>
            </Box>
          ))}
        </Box>

        {/* Zoom */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Typography sx={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Zoom</Typography>
          <Slider
            value={zoom} min={0.4} max={2.2} step={0.1}
            onChange={(_, v) => setZoom(v)}
            sx={{
              width: 72, color: '#E61D50', p: '10px 0',
              '& .MuiSlider-thumb': { width: 10, height: 10, '&:hover': { boxShadow: '0 0 0 6px rgba(230,29,80,0.2)' } },
              '& .MuiSlider-rail': { opacity: 0.2 },
            }}
          />
        </Box>

        <Typography sx={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)', fontVariantNumeric: 'tabular-nums' }}>
          {clips.length} clips &nbsp;·&nbsp; {fmt(totalDur)}
        </Typography>
      </Box>

      {/* ── Mosaic canvas ──────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, minHeight: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '3px', alignContent: 'flex-start' }}>
          {clips.map((row, i) => {
            const dur         = durations[i];
            const sentiment   = sentimentOf(row);
            const spColor     = speakers[row.speaker] || '#6b7280';
            const w           = tileW(dur) * zoom;
            const isSelected  = selected?.clipIdx === i;

            return (
              <Tooltip
                key={row.id ?? i}
                placement="top"
                arrow
                title={
                  <Box sx={{ maxWidth: 260, p: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ fontSize: '10px', fontWeight: 800, color: spColor }}>{row.speaker}</Typography>
                      <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(row.start_in)} – {fmt(row.end_in)} &nbsp;({Math.round(dur)}s)
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>
                      {row.sentence}
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: sentiment.bg }} />
                      <Typography sx={{ fontSize: '9px', color: sentiment.bg, fontWeight: 700 }}>{sentiment.label}</Typography>
                    </Box>
                  </Box>
                }
              >
                <Box
                  onClick={() => selectClip(row, i)}
                  sx={{
                    width: `${w}px`,
                    height: `${tileH}px`,
                    borderRadius: '5px',
                    background: sentiment.dim,
                    border: `1px solid ${sentiment.border}`,
                    borderTop: `2px solid ${spColor}`,
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                    flexShrink: 0,
                    boxShadow: isSelected
                      ? `0 0 0 1.5px ${sentiment.bg}, 0 0 18px ${sentiment.glow}`
                      : 'none',
                    animation: isSelected ? `${playingGlow(sentiment.glow)} 1.8s ease-in-out infinite` : 'none',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 16px ${sentiment.glow}`,
                      border: `1px solid ${sentiment.bg}66`,
                      borderTop: `2px solid ${spColor}`,
                    },
                  }}
                >
                  {/* Left sentiment stripe */}
                  <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '2px', background: sentiment.bg, opacity: 0.6 }} />

                  {/* Content — only render when zoom is big enough to matter */}
                  {zoom >= 0.65 && (
                    <Box sx={{ pl: '6px', pr: '5px', pt: '4px', pb: '3px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '3px' }}>
                        <Typography noWrap sx={{ fontSize: `${Math.round(8 * Math.min(zoom, 1.4))}px`, fontWeight: 700, color: spColor, lineHeight: 1, flex: 1, minWidth: 0 }}>
                          {row.speaker}
                        </Typography>
                        <Typography sx={{ fontSize: `${Math.round(7 * Math.min(zoom, 1.4))}px`, color: 'rgba(255,255,255,0.2)', lineHeight: 1, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                          {Math.round(dur)}s
                        </Typography>
                      </Box>

                      {zoom >= 0.85 && (
                        <Typography sx={{
                          fontSize: `${Math.round(8.5 * Math.min(zoom, 1.5))}px`,
                          color: 'rgba(255,255,255,0.5)',
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: zoom >= 1.5 ? 4 : 2,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {row.sentence}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Playing ripple */}
                  {isSelected && isPlaying && (
                    <Box sx={{
                      position: 'absolute', bottom: 3, right: 4,
                      width: 6, height: 6, borderRadius: '50%',
                      background: sentiment.bg,
                      animation: `${pulse} 1s ease-in-out infinite`,
                    }} />
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* ── Mini player ────────────────────────────────────────────────── */}
      <Box sx={{
        flexShrink: 0,
        borderTop: '1px solid rgba(255,255,255,0.07)',
        background: 'rgba(10,10,18,0.95)',
        backdropFilter: 'blur(12px)',
        display: 'flex',
        gap: 0,
        height: selected ? 130 : 44,
        transition: 'height 0.3s cubic-bezier(0.4,0,0.2,1)',
        overflow: 'hidden',
      }}>
        {!selected ? (
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, gap: 1 }}>
            <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}>
              Click any tile to preview a clip
            </Typography>
          </Box>
        ) : (() => {
          const { row } = selected;
          const sentiment = sentimentOf(row);
          const spColor = speakers[row.speaker] || '#6b7280';
          return (
            <>
              {/* Video */}
              <Box sx={{ width: 160, flexShrink: 0, position: 'relative', background: '#000' }}>
                <video
                  ref={videoRef}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                {/* Progress bar */}
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: 'rgba(255,255,255,0.1)' }}>
                  <Box sx={{ height: '100%', width: `${progress}%`, background: sentiment.bg, transition: 'width 0.1s linear' }} />
                </Box>
              </Box>

              {/* Info + controls */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: '10px 14px', minWidth: 0 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 800, color: spColor }}>{row.speaker}</Typography>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: sentiment.bg }} />
                    <Typography sx={{ fontSize: '9px', color: sentiment.bg, fontWeight: 700 }}>{sentiment.label}</Typography>
                    <Typography sx={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(row.start_in)} – {fmt(row.end_in)}
                    </Typography>
                  </Box>
                  <Typography sx={{
                    fontSize: '11px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.45,
                    overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                  }}>
                    {row.sentence}
                  </Typography>
                </Box>

                {/* Playback controls */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                  {[
                    { label: '⏮', tip: 'Previous clip', action: () => stepClip(-1), disabled: selected.clipIdx === 0 },
                    { label: isPlaying ? '⏸' : '▶', tip: isPlaying ? 'Pause' : 'Play', action: togglePlay, active: true },
                    { label: '⏭', tip: 'Next clip', action: () => stepClip(1), disabled: selected.clipIdx === clips.length - 1 },
                  ].map(({ label, tip, action, active, disabled }) => (
                    <Tooltip key={label} title={tip} placement="top">
                      <Box
                        onClick={disabled ? undefined : action}
                        sx={{
                          width: 26, height: 26, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '12px', cursor: disabled ? 'default' : 'pointer',
                          background: active ? `${sentiment.bg}22` : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${active ? sentiment.bg + '44' : 'rgba(255,255,255,0.08)'}`,
                          color: disabled ? 'rgba(255,255,255,0.2)' : active ? sentiment.bg : 'rgba(255,255,255,0.5)',
                          transition: 'all 0.15s',
                          '&:hover': disabled ? {} : { background: `${sentiment.bg}33`, borderColor: sentiment.bg },
                        }}
                      >
                        {label}
                      </Box>
                    </Tooltip>
                  ))}
                  <Typography sx={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)', ml: 1 }}>
                    {selected.clipIdx + 1} / {clips.length}
                  </Typography>
                </Box>
              </Box>
            </>
          );
        })()}
      </Box>
    </Box>
  );
};

export default MosaicView;
